/**
 * @file cursorStore.ts
 * @description Lightweight Zustand store for high-frequency cursor position data.
 *
 * Cursor positions change at ~20fps and would thrash the main yjsStore if included
 * in remoteUsers. This separate store allows RemoteCursors to subscribe to position
 * changes without triggering re-renders of other components that depend on remoteUsers.
 */
import { create } from 'zustand'

export interface RemoteCursorData {
  x: number
  y: number
  color: string
  alias: string
  userId: string
  /** Active canvas tool (optional for backwards compat with older callers). */
  activeTool?: string
}

interface CursorStore {
  /** Map of clientId → cursor data. Only includes users with non-null cursor positions. */
  cursors: Map<number, RemoteCursorData>
  /** Replace the entire cursors map (called from awareness change handler). */
  setCursors: (cursors: Map<number, RemoteCursorData>) => void
}

export const useCursorStore = create<CursorStore>((set, get) => ({
  cursors: new Map(),
  setCursors: next => {
    const prev = get().cursors
    // Skip update if contents haven't changed (avoids new Map reference on every awareness tick)
    if (prev.size === next.size) {
      let equal = true
      for (const [k, v] of next) {
        const p = prev.get(k)
        if (!p || p.x !== v.x || p.y !== v.y || p.activeTool !== v.activeTool) {
          equal = false
          break
        }
      }
      if (equal) return
    }
    set({ cursors: next })
  },
}))
